import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';

interface NewsArticle {
  id: number;
  category: string;
  date: string;
  title: string;
  description: string;
  image: string;
}

const allNewsArticles: NewsArticle[] = [
  {
    id: 1,
    category: "Platform & Campsite Updates",
    date: "2025.10.08",
    title: "Smart Monitoring Trial Completed at Hok Tau Reservoir",
    description: "Alpha AI and AFCD successfully completed a 7-day A-IoT trial to test solar-powered monitoring devices for real-time campsite management.",
    image: "https://placehold.co/600x400/e2e8f0/64748b?text=Smart+Monitoring"
  },
  {
    id: 2,
    category: "Platform & Campsite Updates",
    date: "2025.10.07",
    title: "New Trails Unlocked: Explore Two Fresh Campsites Near Plover Cove!",
    description: "Two new hiking and camping trails have opened with improved signage, safety zones, and live occupancy tracking.",
    image: "https://placehold.co/600x400/e2e8f0/64748b?text=New+Trails"
  },
  {
    id: 3,
    category: "Nature & Safety Alerts",
    date: "2025.10.06",
    title: "Wildlife Alert at Bride's Pool Area",
    description: "AFCD issued a temporary alert following repeated wild boar sightings near the Bride's Pool picnic site. Visitors are advised to stay cautious.",
    image: "https://placehold.co/600x400/e2e8f0/64748b?text=Wildlife+Alert"
  },
  {
    id: 4,
    category: "Inspiration & Lifestyle",
    date: "2025.10.06",
    title: "Eco-Friendly Camping: Small Habits, Big Impact",
    description: "Learn how simple habits like reusable cutlery and proper waste sorting can help protect Hong Kong's natural beauty.",
    image: "https://placehold.co/600x400/e2e8f0/64748b?text=Eco+Friendly"
  },
  {
    id: 5,
    category: "Inspiration & Lifestyle",
    date: "2025.10.03",
    title: "Weekend Escape: The Best Forest Trails Around Lau Shui Heung",
    description: "Discover scenic forest trails ideal for quiet weekend getaways and morning hikes around the reservoir area.",
    image: "https://placehold.co/600x400/e2e8f0/64748b?text=Forest+Trails"
  },
  {
    id: 6,
    category: "Events & Community",
    date: "2025.10.02",
    title: "Autumn Outdoor Festival — Registration Now Open!",
    description: "Join our upcoming festival featuring stargazing workshops, BBQ challenges, and eco-craft stations. Spots are limited!",
    image: "https://placehold.co/600x400/e2e8f0/64748b?text=Outdoor+Festival"
  }
];

export const AllNews: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2
            className="mb-2"
            style={{
              fontFamily: 'Poppins',
              fontSize: '32px',
              fontWeight: 700,
              color: '#000000'
            }}
          >
            All News
          </h2>
          <p
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              color: '#667479'
            }}
          >
            Explore the latest updates on Hong Kong's campsites — from real-time alerts and park reopenings to smart monitoring breakthroughs and outdoor lifestyle tips.
          </p>
        </div>

        {/* Sort Dropdown */}
        <div className="relative">
          <select
            className="appearance-none bg-white border border-gray-300 text-gray-700 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-black"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            <option>Sort by: Popular</option>
            <option>Sort by: Latest</option>
            <option>Sort by: Oldest</option>
          </select>
          <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 w-4 h-4 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {/* News Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {allNewsArticles.map((article) => (
          <div
            key={article.id}
            onClick={() => navigate(`/news/${article.id}`)}
            className="rounded-2xl overflow-hidden bg-white cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="relative h-56">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="px-3 py-1 bg-black rounded-full"
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#FFFFFF'
                  }}
                >
                  {article.category}
                </span>
                <span
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#667479'
                  }}
                >
                  {article.date}
                </span>
              </div>
              <h3
                className="mb-2"
                style={{
                  fontFamily: 'Poppins',
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: '24px',
                  color: '#000000'
                }}
              >
                {article.title}
              </h3>
              <p
                className="mb-4"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: '#667479'
                }}
              >
                {article.description}
              </p>
              <button
                className="flex items-center gap-2"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#000000'
                }}
              >
                Read more
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2">
        <button
          className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          className="w-10 h-10 flex items-center justify-center bg-black text-white rounded-lg"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          1
        </button>
        <button
          className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          2
        </button>
        <button
          className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          3
        </button>
        <button
          className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          4
        </button>
        <span className="px-2">...</span>
        <button
          className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          28
        </button>
        <button
          className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
          style={{
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
